import React, {useEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import {useMutation, useQuery} from "@apollo/client";
import {toast} from "react-toastify";
import useTranslation from "../../../i18n/useTranslation";
import useThemeColors from "../../organization/portal/useThemeColors";
import {getLocalizedGraphQLError} from "../../../util/graphqlError";
import {confirmDelete} from "../../common/confirmDelete";
import {DELETE_HOCKEY_PLAYER_SCORE, SAVE_HOCKEY_PLAYER_SCORE} from "./SaveHockeyPlayerScore";
import {GET_HOCKEY_GAME_TIMELINE} from "./GetHockeyGameTimeline";
import GameEventTimeline, {GameEventTimelineStyles} from "../GameEventTimeline";
import GameMatchboard from "../GameMatchboard";
import {getHockeyPeriodOptions, isAllowedHockeyClockInput, validateHockeyEventClock} from "./hockeyClock";

const ACTION_TYPES = [
    "goal", "assist", "shot", "penalty", "faceoff_win", "faceoff_loss", "hit",
    "blocked_shot", "takeaway", "giveaway", "save", "goal_against", "substitution_in", "substitution_out",
];

const PENALTY_TYPES = ["minor", "major", "misconduct", "game_misconduct", "match"];

const EMPTY_FORM = {
    teamId: "",
    playerId: "",
    relatedPlayerId: "",
    actionType: "",
    penaltyType: "",
    penaltyMinutes: "",
    period: "1",
    minute: "",
    second: "",
    notes: "",
};

function getHockeyEventConfig(t, actionType) {
    const key = String(actionType || "").toLowerCase();
    const eventStyles = {
        goal: {icon: "fas fa-bullseye", accent: "#12824a", soft: "rgba(18,130,74,.12)", glow: "rgba(18,130,74,.16)"},
        penalty: {icon: "fas fa-gavel", accent: "#c99700", soft: "rgba(255,215,0,.18)", glow: "rgba(201,151,0,.12)"},
        assist: {icon: "fas fa-hands-helping", accent: "#2563eb", soft: "rgba(37,99,235,.12)", glow: "rgba(37,99,235,.14)"},
        save: {icon: "fas fa-hand-paper", accent: "#7c3aed", soft: "rgba(124,58,237,.12)", glow: "rgba(124,58,237,.14)"},
    };
    return {
        ...(eventStyles[key] || {icon: "fas fa-hockey-puck", accent: "#14151a", soft: "rgba(0,0,0,.06)", glow: "rgba(0,0,0,.06)"}),
        label: t(`gameElement.hockeyGameSheet.eventTypes.${key}`, {}, key.replaceAll("_", " ")),
        shortLabel: key.replaceAll("_", " ").toUpperCase(),
    };
}

export default function HockeyGameDetailsModal({open, onClose, gameId, homeTeam, visitorTeam, scoreHome, scoreVisitor, canEdit, onScoreChange}) {
    const {t} = useTranslation();
    const colors = useThemeColors();
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState("all");
    const [currentScore, setCurrentScore] = useState({home: scoreHome ?? 0, visitor: scoreVisitor ?? 0});
    const defaultTeamId = String(homeTeam?.id || visitorTeam?.id || "");

    useEffect(() => {
        if (open) setForm({...EMPTY_FORM, teamId: defaultTeamId});
    }, [open, defaultTeamId]);

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        if (open) {
            document.body.classList.add("modal-open");
            document.body.style.overflow = "hidden";
        } else {
            document.body.classList.remove("modal-open");
        }

        return () => {
            document.body.classList.remove("modal-open");
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    const teamOptions = useMemo(() => [
        visitorTeam?.id ? {id: String(visitorTeam.id), name: visitorTeam.name || t("gameElement.hockeyGameSheet.visitor")} : null,
        homeTeam?.id ? {id: String(homeTeam.id), name: homeTeam.name || t("gameElement.hockeyGameSheet.home")} : null,
    ].filter(Boolean), [homeTeam, visitorTeam, t]);

    const {data, loading, error, refetch} = useQuery(GET_HOCKEY_GAME_TIMELINE, {
        variables: {gameId: parseInt(gameId, 10), teamId: form.teamId ? parseInt(form.teamId, 10) : null},
        skip: !open || !gameId || !form.teamId,
        fetchPolicy: "cache-and-network",
    });
    const [saveScore] = useMutation(SAVE_HOCKEY_PLAYER_SCORE);
    const [deleteScore] = useMutation(DELETE_HOCKEY_PLAYER_SCORE);

    const players = (data?.getTeamRosterForLineup || []).map((row) => ({
        id: String(row?.player?.id || ""),
        name: row?.player?.fullName || "",
    })).filter((row) => row.id);
    const events = useMemo(() => data?.hockeyPlayerScoresByGame || [], [data?.hockeyPlayerScoresByGame]);
    const periodOptions = useMemo(() => getHockeyPeriodOptions(events), [events]);

    useEffect(() => {
        setCurrentScore({home: scoreHome ?? 0, visitor: scoreVisitor ?? 0});
    }, [scoreHome, scoreVisitor]);

    const applyGoalDelta = (teamId, delta) => {
        const nextScore = {
            home: currentScore.home + (String(teamId) === String(homeTeam?.id) ? delta : 0),
            visitor: currentScore.visitor + (String(teamId) === String(visitorTeam?.id) ? delta : 0),
        };
        setCurrentScore(nextScore);
        onScoreChange?.(nextScore.home, nextScore.visitor);
    };
    const periods = useMemo(() => {
        const values = new Set([1, 2, 3]);
        events.forEach((event) => values.add(Number(event.period)));
        return Array.from(values).filter(Number.isFinite).sort((a, b) => a - b);
    }, [events]);
    const visibleEvents = useMemo(() => {
        const filteredEvents = selectedPeriod === "all"
            ? events
            : events.filter((event) => Number(event.period) === Number(selectedPeriod));

        return [...filteredEvents].sort((firstEvent, secondEvent) => {
            const periodDifference = Number(secondEvent.period || 0) - Number(firstEvent.period || 0);
            if (periodDifference !== 0) return periodDifference;

            const minuteDifference = Number(secondEvent.minute || 0) - Number(firstEvent.minute || 0);
            if (minuteDifference !== 0) return minuteDifference;

            return Number(secondEvent.second || 0) - Number(firstEvent.second || 0);
        });
    }, [events, selectedPeriod]);

    useEffect(() => {
        if (selectedPeriod !== "all" && !periods.includes(Number(selectedPeriod))) {
            setSelectedPeriod("all");
        }
    }, [periods, selectedPeriod]);

    const updateForm = (key, value) => {
        setForm((current) => ({
            ...current,
            [key]: value,
            ...(key === "teamId" ? {playerId: "", relatedPlayerId: ""} : {}),
        }));
    };

    const updateClockField = (key, value) => {
        if (!isAllowedHockeyClockInput(key, value, form.minute)) return;

        setForm((current) => ({
            ...current,
            [key]: value,
            ...(key === "minute" && value === "20" ? {second: "0"} : {}),
        }));
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!form.teamId || !form.playerId || !form.actionType || form.minute === "" || form.period === "") {
            toast.warning(t("gameElement.hockeyGameSheet.required"));
            return;
        }
        const second = form.second === "" ? 0 : Number(form.second);
        const clockError = validateHockeyEventClock(form.period, form.minute, second);
        if (clockError) {
            toast.warning(t(`gameElement.hockeyGameSheet.${clockError}`));
            return;
        }
        const period = Number(form.period);
        const minute = Number(form.minute);
        setSaving(true);
        try {
            await saveScore({variables: {input: {
                gameId: parseInt(gameId, 10),
                teamId: parseInt(form.teamId, 10),
                playerId: parseInt(form.playerId, 10),
                relatedPlayerId: form.relatedPlayerId ? parseInt(form.relatedPlayerId, 10) : null,
                actionType: form.actionType,
                penaltyType: form.actionType === "penalty" ? form.penaltyType || null : null,
                penaltyMinutes: form.actionType === "penalty" && form.penaltyMinutes !== "" ? parseInt(form.penaltyMinutes, 10) : null,
                period,
                minute,
                second,
                notes: form.notes || "",
            }}});
            toast.success(t("gameElement.hockeyGameSheet.saveSuccess"));
            setForm((current) => ({...EMPTY_FORM, teamId: current.teamId}));
            if (form.actionType === "goal") applyGoalDelta(form.teamId, 1);
            await refetch();
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"));
        } finally {
            setSaving(false);
        }
    };

    const remove = (scoreId) => {
        const removedEvent = events.find((score) => String(score.id) === String(scoreId));
        if (!removedEvent) return;

        confirmDelete({
            title: t("gameElement.hockeyGameSheet.confirmDeleteTitle"),
            message: t("gameElement.hockeyGameSheet.confirmDeleteMessage", {
                action: t(`gameElement.hockeyGameSheet.eventTypes.${String(removedEvent.actionType).toLowerCase()}`),
                period: removedEvent.period,
                time: `${removedEvent.minute}:${String(removedEvent.second || 0).padStart(2, "0")}`,
            }),
            confirmLabel: t("gameElement.hockeyGameSheet.confirmDeleteConfirm"),
            cancelLabel: t("gameElement.hockeyGameSheet.confirmDeleteCancel"),
            danger: true,
            onConfirm: async () => {
                try {
                    await deleteScore({variables: {scoreId}});
                    if (String(removedEvent.actionType).toLowerCase() === "goal") {
                        applyGoalDelta(removedEvent.team?.id, -1);
                    }
                    await refetch();
                } catch (err) {
                    toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"));
                }
            },
        });
    };

    const renderEventBody = (event, cfg) => (
        <>
            <div className="kbola-feedHeroRow">
                <div className="kbola-feedHeroPlayer">{event?.player?.fullName || "-"}</div>
                {cfg.shortLabel ? <span className="kbola-feedMiniTag">{cfg.shortLabel}</span> : null}
            </div>
            {event?.relatedPlayer?.fullName ? (
                <div className="kbola-feedLinked">
                    <i className="fas fa-link mr-2"/>
                    {t("gameElement.hockeyGameSheet.relatedPlayerSummary", {player: event.relatedPlayer.fullName})}
                </div>
            ) : null}
            {event?.penaltyMinutes ? (
                <div className="kbola-feedNotes">
                    <i className="fas fa-stopwatch mr-2"/>
                    {t("gameElement.hockeyGameSheet.penaltyMinutesSummary", {minutes: event.penaltyMinutes})}
                </div>
            ) : null}
            {String(event?.notes || "").trim() ? (
                <div className="kbola-feedNotes">
                    <i className="far fa-sticky-note mr-2"/>
                    {event.notes}
                </div>
            ) : null}
        </>
    );

    if (!open || !mounted) return null;

    const eventLabel = (value) => value.replaceAll("_", " ");

    const modalNode = (
        <>
        <div className="modal fade show kbola-hockeyModal" style={{display: "block"}} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
                <div className="modal-content kbola-hockeyContent">
                    <div className="modal-header kbola-hockeyHeader">
                        <div className="kbola-hockeyHeaderIdentity">
                            <span className="kbola-hockeyBadge"><i className="fas fa-hockey-puck"/></span>
                            <div>
                            <span className="kbola-hockeyEyebrow">{t("gameElement.enterDetails")}</span>
                                <h4 className="modal-title">{t("gameElement.hockeyGameSheet.title")}</h4>
                            </div>
                        </div>
                        <button type="button" className="close kbola-hockeyClose" onClick={onClose} aria-label={t("gameElement.hockeyGameSheet.closeAria")}><span aria-hidden="true">&times;</span></button>
                    </div>
                    <div className="modal-body kbola-hockeyBody">
                        <GameMatchboard
                            visitorTeam={visitorTeam}
                            homeTeam={homeTeam}
                            visitorScore={currentScore.visitor}
                            homeScore={currentScore.home}
                            visitorLabel={t("gameElement.hockeyGameSheet.visitorShort")}
                            homeLabel={t("gameElement.hockeyGameSheet.homeShort")}
                        />

                        <div className={`kbola-hockeyLayout ${!canEdit ? "is-view-only" : ""}`}>
                            <section className="kbola-hockeyPanel kbola-hockeyTimelinePanel">
                                <div className="kbola-hockeyPanelHeader"><div><span className="kbola-hockeyPanelEyebrow">{t("gameElement.hockeyGameSheet.gameFeed")}</span><h5>{t("gameElement.hockeyGameSheet.timeline")}</h5></div><span className="kbola-hockeyCount">{events.length}</span></div>
                                <div className="kbola-hockeyPeriodTabs" role="tablist" aria-label={t("gameElement.hockeyGameSheet.timeline")}>
                                    <button type="button" className={selectedPeriod === "all" ? "is-active" : ""} onClick={() => setSelectedPeriod("all")}>{t("gameElement.hockeyGameSheet.allPeriods")}</button>
                                    {periods.map((period) => <button type="button" key={period} className={Number(selectedPeriod) === period ? "is-active" : ""} onClick={() => setSelectedPeriod(String(period))}>{period > 3 ? t("gameElement.hockeyGameSheet.overtimeShort", {period: period - 3}) : t("gameElement.hockeyGameSheet.periodShort", {period})}</button>)}
                                </div>
                                <GameEventTimeline
                                    events={visibleEvents}
                                    loading={loading}
                                    error={error}
                                    canEdit={canEdit}
                                    onDelete={remove}
                                    getEventConfig={(event) => getHockeyEventConfig(t, event.actionType)}
                                    getSide={(event) => String(event.team?.id) === String(homeTeam?.id) ? "right" : "left"}
                                    getTimeLabel={(event) => `${event.minute}:${String(event.second || 0).padStart(2, "0")}`}
                                    renderEventBody={renderEventBody}
                                    visitorName={visitorTeam?.name || t("gameElement.hockeyGameSheet.visitor")}
                                    homeName={homeTeam?.name || t("gameElement.hockeyGameSheet.home")}
                                    timelineLabel={t("gameElement.hockeyGameSheet.timeline")}
                                    loadingContent={<><i className="fas fa-spinner fa-spin mr-2"/>{t("gameElement.hockeyGameSheet.loading")}</>}
                                    errorContent={t("gameElement.hockeyGameSheet.loadFailed")}
                                    emptyContent={<><i className="fas fa-snowflake mr-2"/>{events.length ? t("gameElement.hockeyGameSheet.noEventsInPeriod") : t("gameElement.hockeyGameSheet.noEvents")}</>}
                                />
                                <GameEventTimelineStyles scope=".kbola-hockeyTimelinePanel .kbola-gameEventTimeline"/>
                            </section>

                            {canEdit && <section className="kbola-hockeyPanel kbola-hockeyFormPanel">
                                <div className="kbola-hockeyPanelHeader"><div><span className="kbola-hockeyPanelEyebrow">{t("gameElement.hockeyGameSheet.eventEntry")}</span><h5>{t("gameElement.hockeyGameSheet.addEvent")}</h5></div><i className="fas fa-plus-circle kbola-hockeyHeaderIcon"/></div>
                                <form onSubmit={submit}>
                                    <div className="kbola-hockeyFormGrid">
                                        <div className="kbola-hockeyField kbola-hockeyFieldWide"><label>{t("gameElement.hockeyGameSheet.team")}</label><select className="form-control kbola-hockeyControl" value={form.teamId} onChange={(e) => updateForm("teamId", e.target.value)}>
                                        <option value="">{t("gameElement.hockeyGameSheet.select")}</option>
                                        {teamOptions.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                                    </select></div>
                                        <div className="kbola-hockeyField kbola-hockeyFieldWide"><label>{t("gameElement.hockeyGameSheet.action")}</label><select className="form-control kbola-hockeyControl" value={form.actionType} onChange={(e) => updateForm("actionType", e.target.value)}>
                                        <option value="">{t("gameElement.hockeyGameSheet.select")}</option>
                                        {ACTION_TYPES.map((action) => <option key={action} value={action}>{t(`gameElement.hockeyGameSheet.eventTypes.${action}`, {}, eventLabel(action))}</option>)}
                                    </select></div>
                                        <div className="kbola-hockeyField kbola-hockeyFieldWide"><label>{t("gameElement.hockeyGameSheet.player")}</label><select className="form-control kbola-hockeyControl" value={form.playerId} onChange={(e) => updateForm("playerId", e.target.value)}>
                                        <option value="">{t("gameElement.hockeyGameSheet.select")}</option>
                                        {players.map((player) => <option key={player.id} value={player.id}>{player.name}</option>)}
                                    </select></div>
                                        <div className="kbola-hockeyField kbola-hockeyFieldWide"><label>{t("gameElement.hockeyGameSheet.relatedPlayer")} <em>{t("gameElement.hockeyGameSheet.optional")}</em></label><select className="form-control kbola-hockeyControl" value={form.relatedPlayerId} onChange={(e) => updateForm("relatedPlayerId", e.target.value)}>
                                        <option value="">{t("gameElement.hockeyGameSheet.optionalSelect")}</option>
                                        {players.map((player) => <option key={player.id} value={player.id}>{player.name}</option>)}
                                    </select></div>
                                        <div className="kbola-hockeyField"><label>{t("gameElement.hockeyGameSheet.period")}</label><select className="form-control kbola-hockeyControl" value={form.period} onChange={(e) => updateForm("period", e.target.value)}>{periodOptions.map((period) => <option key={period} value={period}>{period > 3 ? t("gameElement.hockeyGameSheet.overtimeShort", {period: period - 3}) : t("gameElement.hockeyGameSheet.periodShort", {period})}</option>)}</select></div>
                                        <div className="kbola-hockeyField"><label>{t("gameElement.hockeyGameSheet.minute")}</label><input className="form-control kbola-hockeyControl" type="number" min="0" max="20" step="1" value={form.minute} onChange={(e) => updateClockField("minute", e.target.value)}/></div>
                                        <div className="kbola-hockeyField"><label>{t("gameElement.hockeyGameSheet.second")}</label><input className="form-control kbola-hockeyControl" type="number" min="0" max={form.minute === "20" ? 0 : 59} step="1" value={form.second} onChange={(e) => updateClockField("second", e.target.value)}/></div>
                                {form.actionType === "penalty" && <>
                                    <div className="kbola-hockeyField"><label>{t("gameElement.hockeyGameSheet.penaltyType")}</label><select className="form-control kbola-hockeyControl" value={form.penaltyType} onChange={(e) => updateForm("penaltyType", e.target.value)}><option value="">{t("gameElement.hockeyGameSheet.select")}</option>{PENALTY_TYPES.map((type) => <option key={type} value={type}>{t(`gameElement.hockeyGameSheet.penaltyTypes.${type}`, {}, eventLabel(type))}</option>)}</select></div>
                                    <div className="kbola-hockeyField"><label>{t("gameElement.hockeyGameSheet.penaltyMinutes")}</label><input className="form-control kbola-hockeyControl" type="number" min="0" value={form.penaltyMinutes} onChange={(e) => updateForm("penaltyMinutes", e.target.value)}/></div>
                                </>}
                                        <div className="kbola-hockeyField kbola-hockeyFieldFull"><label>{t("gameElement.hockeyGameSheet.notes")} <em>{t("gameElement.hockeyGameSheet.optional")}</em></label><textarea rows="3" className="form-control kbola-hockeyControl" value={form.notes} onChange={(e) => updateForm("notes", e.target.value)}/></div>
                                    </div>
                                    <button className="btn btn-kbola kbola-primaryBtn kbola-hockeySave" type="submit" disabled={saving}><i className={`fas ${saving ? "fa-spinner fa-spin" : "fa-save"}`}/> {saving ? t("gameElement.hockeyGameSheet.saving") : t("gameElement.hockeyGameSheet.saveEvent")}</button>
                                </form>
                            </section>}
                        </div>
                    </div>
                    <div className="modal-footer kbola-hockeyFooter"><button type="button" className="btn btn-default kbola-secondaryBtn" onClick={onClose}>{t("gameElement.hockeyGameSheet.close")}</button></div>
                </div>
            </div>
        </div>
        <div className="modal-backdrop fade show kbola-hockeyBackdrop" onClick={onClose}/>
        <style>{`
            .kbola-hockeyModal { position: fixed; inset: 0; z-index: 1055; overflow-y: auto !important; font-family: 'Poppins', sans-serif; }
            .kbola-hockeyModal .modal-dialog { width: calc(100vw - 2rem); max-width: 1500px; min-height: calc(100vh - 2rem); max-height: calc(100vh - 2rem); margin: 1rem auto; }
            .kbola-hockeyContent { display: flex; flex-direction: column; max-height: calc(100vh - 2rem); overflow: hidden !important; border: 1px solid rgba(0,0,0,.08); border-radius: 20px; box-shadow: 0 22px 60px rgba(0,0,0,.22); }
            .kbola-hockeyHeader { padding: 17px 20px; color: ${colors.textOnPrimary}; background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary}); border: 0; }
            .kbola-hockeyHeaderIdentity { display: flex; align-items: center; gap: 12px; }
            .kbola-hockeyBadge { display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; border-radius: 14px; color: ${colors.primary}; background: #fff; box-shadow: 0 7px 16px rgba(0,0,0,.14); }
            .kbola-hockeyEyebrow, .kbola-hockeyPanelEyebrow { display: block; font-size: 10px; font-weight: 900; letter-spacing: .12em; text-transform: uppercase; opacity: .72; }
            .kbola-hockeyHeader .modal-title { font-size: 18px; font-weight: 900; }
            .kbola-hockeyClose { color: ${colors.textOnPrimary}; opacity: 1; }
            .kbola-hockeyBody { min-height: 0; max-height: calc(100vh - 210px) !important; overflow-y: auto !important; -webkit-overflow-scrolling: touch; padding: 18px; background: linear-gradient(180deg, rgba(0,0,0,.018), #fff 35%); }
            .kbola-hockeyLayout { display: grid; grid-template-columns: minmax(320px,1fr) minmax(0,2fr); gap: 16px; }
            .kbola-hockeyLayout.is-view-only { grid-template-columns: 1fr; }
            .kbola-hockeyLayout.is-view-only .kbola-hockeyTimelinePanel { grid-column: 1; }
            .kbola-hockeyTimelinePanel { grid-column: 2; grid-row: 1; }
            .kbola-hockeyFormPanel { grid-column: 1; grid-row: 1; }
            .kbola-hockeyPanel { min-width: 0; padding: 16px; border: 1px solid rgba(0,0,0,.07); border-radius: 20px; background: rgba(255,255,255,.94); box-shadow: 0 10px 24px rgba(0,0,0,.05); }
            .kbola-hockeyPanelHeader { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding-bottom: 12px; margin-bottom: 10px; border-bottom: 1px solid rgba(0,0,0,.07); }
            .kbola-hockeyPanelHeader h5 { margin: 2px 0 0; font-size: 17px; font-weight: 900; }
            .kbola-hockeyPanelEyebrow { color: ${colors.primary}; opacity: 1; }
            .kbola-hockeyCount { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; padding: 0 8px; border-radius: 999px; color: ${colors.primary}; background: ${colors.primary}18; font-weight: 900; }
            .kbola-hockeyHeaderIcon { color: ${colors.primary}; font-size: 20px; }
            .kbola-hockeyPeriodTabs { display: flex; gap: 6px; margin: 0 0 14px; padding: 4px; overflow-x: auto; border-radius: 12px; background: #f5f7fa; }
            .kbola-hockeyPeriodTabs button { min-width: 44px; padding: 7px 11px; border: 0; border-radius: 9px; background: transparent; color: #667085; font-size: 11px; font-weight: 900; white-space: nowrap; }
            .kbola-hockeyPeriodTabs button:hover { color: ${colors.primary}; background: #fff; }
            .kbola-hockeyPeriodTabs button.is-active { color: ${colors.textOnPrimary}; background: ${colors.primary}; box-shadow: 0 5px 12px ${colors.primary}35; }
            .kbola-hockeyTimelineHeader { display: grid; grid-template-columns: minmax(0,1fr) 76px minmax(0,1fr); align-items: center; gap: 12px; margin: 2px 0 5px; color: #98a2b3; font-size: 9px; font-weight: 900; letter-spacing: .1em; text-transform: uppercase; }
            .kbola-hockeyTimelineHeader .is-left { text-align: right; }
            .kbola-hockeyTimelineHeader .is-right { text-align: left; }
            .kbola-hockeyTimeline { position: relative; max-height: 470px; overflow-y: auto; padding: 6px 2px 10px; }
            .kbola-hockeyTimelineRail { position: absolute; top: 0; bottom: 0; left: 50%; width: 2px; transform: translateX(-50%); background: linear-gradient(${colors.primary}22, ${colors.primary}65, ${colors.primary}22); }
            .kbola-hockeyTimelineRow { position: relative; display: grid; grid-template-columns: minmax(0,1fr) 76px minmax(0,1fr); align-items: center; min-height: 78px; }
            .kbola-hockeyTimelineSide { min-width: 0; }
            .kbola-hockeyTimelineSide.is-left { padding-right: 12px; text-align: right; }
            .kbola-hockeyTimelineSide.is-right { padding-left: 12px; text-align: left; }
            .kbola-hockeyTimelineCenter { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 4px; color: #667085; font-size: 10px; font-weight: 900; }
            .kbola-hockeyTimelineIcon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border: 4px solid #fff; border-radius: 50%; color: ${colors.primary}; background: ${colors.primary}18; box-shadow: 0 3px 10px rgba(0,0,0,.12); }
            .kbola-hockeyTimelineIcon.is-goal { color: #12824a; background: #12824a20; }
            .kbola-hockeyTimelineIcon.is-penalty { color: #c99700; background: #c9970020; }
            .kbola-hockeyEventCard { position: relative; overflow: hidden; padding: 14px 60px 14px 15px; border: 1px solid rgba(0,0,0,.07); border-radius: 20px; background: linear-gradient(180deg, #fff 0%, #f8fafc 100%); box-shadow: 0 12px 24px rgba(0,0,0,.05), 0 2px 8px rgba(0,0,0,.03); }
            .kbola-hockeyTimelineSide.is-left .kbola-hockeyEventCard::after, .kbola-hockeyTimelineSide.is-right .kbola-hockeyEventCard::after { content: ""; position: absolute; top: 0; bottom: 0; width: 5px; background: ${colors.secondary}; }
            .kbola-hockeyTimelineSide.is-left .kbola-hockeyEventCard::after { right: 0; }
            .kbola-hockeyTimelineSide.is-right .kbola-hockeyEventCard::after { left: 0; background: ${colors.primary}; }
            .kbola-hockeyEventCard.is-goal { border-color: #12824a66; }
            .kbola-hockeyEventCard.is-goal::after { background: #12824a !important; }
            .kbola-hockeyEventCard.is-penalty { border-color: #c9970066; }
            .kbola-hockeyEventCard.is-penalty::after { background: #c99700 !important; }
            .kbola-hockeyEventCardTop { display: flex; align-items: flex-start; gap: 10px; justify-content: space-between; margin-bottom: 12px; }
            .kbola-hockeyEventType { display: inline-flex; align-items: center; min-width: 0; gap: 6px; }
            .kbola-hockeyTeamTag { display: inline-flex; align-items: center; min-height: 20px; padding: 0 7px; border: 1px solid rgba(0,0,0,.08); border-radius: 999px; color: ${colors.secondary}; background: ${colors.secondary}16; font-size: 8px; font-weight: 900; letter-spacing: .06em; }
            .kbola-hockeyTimelineSide.is-right .kbola-hockeyTeamTag { color: ${colors.primary}; background: ${colors.primary}16; }
            .kbola-hockeyCardIcon { display: inline-flex; align-items: center; justify-content: center; width: 27px; height: 27px; flex: 0 0 auto; border-radius: 9px; color: ${colors.secondary}; background: ${colors.secondary}16; font-size: 11px; }
            .kbola-hockeyTimelineSide.is-right .kbola-hockeyCardIcon { color: ${colors.primary}; background: ${colors.primary}16; }
            .kbola-hockeyEventCard.is-goal .kbola-hockeyCardIcon { color: #12824a; background: #12824a1c; }
            .kbola-hockeyEventCard.is-penalty .kbola-hockeyCardIcon { color: #c99700; background: #c997001c; }
            .kbola-hockeyEventCardTop strong { color: rgba(20,21,26,.84); font-size: 13px; font-weight: 900; line-height: 1.2; text-transform: capitalize; }
            .kbola-hockeyEventCardTop span { overflow: hidden; color: #8a95a3; font-size: 9px; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
            .kbola-hockeyEventPlayer { margin-top: 4px; overflow: hidden; color: rgba(20,21,26,.95); font-size: 15px; font-weight: 900; line-height: 1.25; text-overflow: ellipsis; white-space: nowrap; }
            .kbola-hockeyEventCard small { display: block; margin-top: 4px; color: #c99700; font-size: 9px; font-weight: 900; }
            .kbola-hockeyTimelineSide.is-left .kbola-hockeyEventCardTop { flex-direction: row-reverse; }
            .kbola-hockeyEventCard .kbola-hockeyDelete { position: absolute; top: 12px; right: 12px; }
            .kbola-hockeyEvents { display: flex; flex-direction: column; gap: 8px; max-height: 430px; overflow-y: auto; padding-right: 3px; }
            .kbola-hockeyEvent { display: grid; grid-template-columns: 48px 34px minmax(0,1fr) 28px; align-items: center; gap: 10px; padding: 10px; border-radius: 14px; background: linear-gradient(135deg, rgba(0,0,0,.025), #fff); border: 1px solid rgba(0,0,0,.06); }
            .kbola-hockeyEventTime { text-align: center; color: rgba(20,21,26,.55); font-size: 11px; }
            .kbola-hockeyEventTime strong, .kbola-hockeyEventTime span { display: block; }
            .kbola-hockeyEventIcon { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 10px; color: ${colors.primary}; background: ${colors.primary}18; }
            .kbola-hockeyEventBody { min-width: 0; }
            .kbola-hockeyEventBody strong, .kbola-hockeyEventBody span, .kbola-hockeyEventBody small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
            .kbola-hockeyEventBody strong { font-size: 13px; text-transform: capitalize; }
            .kbola-hockeyEventBody span { color: rgba(20,21,26,.6); font-size: 11px; }
            .kbola-hockeyEventBody small { color: #c99700; font-size: 10px; font-weight: 800; }
            .kbola-hockeyDelete { width: 34px; height: 34px; padding: 0; border-radius: 11px; border: 1px solid rgba(220,53,69,.18); background: rgba(220,53,69,.06); color: rgba(220,53,69,.95); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; opacity: 1; }
            .kbola-hockeyDelete:hover { background: rgba(220,53,69,.12); color: #dc3545; }
            .kbola-hockeyEmpty, .kbola-hockeyNotice { display: flex; align-items: center; justify-content: center; gap: 9px; min-height: 140px; color: rgba(20,21,26,.52); text-align: center; }
            .kbola-hockeyEmpty i { color: ${colors.primary}; font-size: 22px; }
            .kbola-hockeyFormGrid { display: grid; grid-template-columns: repeat(6, minmax(0,1fr)); gap: 11px; }
            .kbola-hockeyField { grid-column: span 2; min-width: 0; }
            .kbola-hockeyFieldWide { grid-column: span 3; }
            .kbola-hockeyFieldFull { grid-column: 1 / -1; }
            .kbola-hockeyField label { display: block; margin-bottom: 8px; color: rgba(20,21,26,.72); font-size: 13px; font-weight: 900; }
            .kbola-hockeyField label em { color: rgba(20,21,26,.5); font-size: 11px; font-style: normal; font-weight: 700; }
            .kbola-hockeyControl { min-height: 44px; padding: 10px; border: 1px solid rgba(0,0,0,.1); border-radius: 13px; background: rgba(255,255,255,.96); box-shadow: none; font-size: 13px; font-weight: 800; }
            .kbola-hockeyControl:focus { outline: none; border-color: rgba(var(--kbs-primary-rgb, 0,173,255),.75); box-shadow: 0 0 0 .2rem rgba(var(--kbs-primary-rgb, 0,173,255),.18); }
            .kbola-primaryBtn { border-radius: 12px; border: 1px solid rgba(0,0,0,.1); padding: 10px 14px; background: ${colors.primary}; color: ${colors.textOnPrimary}; font-weight: 900; }
            .kbola-primaryBtn:hover { color: ${colors.textOnPrimary}; filter: brightness(.96); }
            .kbola-secondaryBtn { border-radius: 12px; border: 1px solid rgba(0,0,0,.1); padding: 10px 14px; background: #f3f5f7; color: #4b5563; font-weight: 900; }
            .kbola-secondaryBtn:hover { background: #e9edf1; color: #263241; }
            .kbola-hockeySave { width: 100%; margin-top: 14px; }
            .kbola-hockeyFooter { padding: 12px 16px; border-top: 1px solid rgba(0,0,0,.06); background: rgba(0,0,0,.015); }
            .kbola-hockeyBackdrop { z-index: 1050; }
            @media (max-width: 900px) { .kbola-hockeyLayout { grid-template-columns: 1fr; } .kbola-hockeyTimelinePanel, .kbola-hockeyFormPanel { grid-column: 1; grid-row: auto; } }
            @media (max-width: 576px) { .kbola-hockeyFormGrid { grid-template-columns: 1fr 1fr; } .kbola-hockeyField, .kbola-hockeyFieldWide, .kbola-hockeyFieldFull { grid-column: span 2; } .kbola-hockeyTimelineRow { grid-template-columns: minmax(0,1fr) 54px minmax(0,1fr); } .kbola-hockeyTimelineSide.is-left { padding-right: 7px; } .kbola-hockeyTimelineSide.is-right { padding-left: 7px; } .kbola-hockeyEventCard { padding: 10px 48px 10px 10px; } .kbola-hockeyEventCardTop span { display: none; } .kbola-hockeyEventCard .kbola-hockeyDelete { top: 9px; right: 9px; } }
        `}</style>
        </>
    );

    return createPortal(modalNode, document.body);
}
